/**
 * Тест на уровень for the active course's language: an intro, 12–15 adaptive questions
 * (words, grammar, reading, listening) with a «Не знаю» option, then the result with the
 * recommended start. An optional open answer can be judged by the tutor when the device
 * has a Gemini key. The result opens every lesson before the recommended start.
 */
import { useEffect, useMemo, useState } from 'react';
import { routes, useRouter } from '../state/router.tsx';
import { useAppData, useLoadedData } from '../state/app-data.tsx';
import { useSession } from '../state/session.tsx';
import {
  PLACEMENT_MAX_ITEMS,
  nextItem,
  placementResult,
  recommendStart,
  shouldStop,
  type PlacementBank,
  type PlacementItem,
  type PlacementOutcome,
  type PlacementResponse,
  type PlacementResult,
  type PlacementSkill,
} from '../core/placement.ts';
import {
  PLACEMENT_LANGS,
  cachedPlacements,
  dismissPlacement,
  loadPlacementBank,
  placementDismissed,
  savePlacement,
  usePlacements,
  type PlacementRecord,
} from '../data/placement.ts';
import { askJson, getGeminiKey } from '../data/gemini.ts';
import { RoboCat, type RoboCatState } from '../ui/RoboCat.tsx';
import { Icon } from '../ui/Icon.tsx';
import { languageInfo } from '../ui/course.ts';
import { canSpeak, speak } from '../ui/speech.ts';
import { playFeedback } from '../ui/feedback.ts';
import './lesson/lesson.css';
import './placement.css';

const SKILL_RU: Record<PlacementSkill, string> = {
  vocab: 'Слова',
  grammar: 'Грамматика',
  reading: 'Чтение',
  listening: 'На слух',
};

const SKILL_RU_LOWER: Record<PlacementSkill, string> = {
  vocab: 'слова',
  grammar: 'грамматика',
  reading: 'чтение',
  listening: 'понимание на слух',
};

type Phase =
  | { kind: 'intro' }
  | { kind: 'question'; item: PlacementItem }
  | { kind: 'result'; result: PlacementResult; record: PlacementRecord };

export function PlacementScreen() {
  const { navigate } = useRouter();
  const userId = useSession().session?.user.id ?? '';
  const { activeCourse } = useAppData();
  const { books, lessons } = useLoadedData();
  const lang = activeCourse?.lang ?? '';
  const language = languageInfo(lang);
  const [bank, setBank] = useState<PlacementBank | null | undefined>(undefined);
  const [phase, setPhase] = useState<Phase>({ kind: 'intro' });
  const [responses, setResponses] = useState<PlacementResponse[]>([]);
  const [picked, setPicked] = useState<{ option: string | null; outcome: PlacementOutcome } | null>(null);
  const [cat, setCat] = useState<RoboCatState>('idle');
  // A fresh seed per attempt, so «Пройти заново» does not repeat the same questions.
  const [seed] = useState(() => `${Date.now()}`);
  const speakable = canSpeak();

  useEffect(() => {
    let active = true;
    loadPlacementBank(lang)
      .then((b) => active && setBank(b))
      .catch(() => active && setBank(null));
    return () => {
      active = false;
    };
  }, [lang]);

  const close = () => navigate(routes.home);

  if (bank === undefined) {
    return (
      <div className="placement placement--center">
        <RoboCat size={96} state="thinking" />
        <p className="t-meta">Готовим вопросы…</p>
      </div>
    );
  }

  if (bank === null) {
    return (
      <div className="placement placement--center">
        <RoboCat size={96} state="sad" />
        <h1 className="t-card">Для этого языка теста пока нет</h1>
        <button type="button" className="btn btn--primary btn--block" onClick={close}>
          На главную
        </button>
      </div>
    );
  }

  const levels = bank.scale.length;

  const startQuestions = () => {
    const item = nextItem(bank, [], seed, { listening: speakable });
    if (item) setPhase({ kind: 'question', item });
  };

  const skip = () => {
    dismissPlacement(userId, lang);
    close();
  };

  const finish = (all: PlacementResponse[]) => {
    const result = placementResult(bank, all);
    const start = recommendStart(result.mean, books, lessons);
    const record: PlacementRecord = {
      lang,
      level_label: result.label,
      taken_at: new Date().toISOString(),
      score: {
        version: bank.version,
        level: result.level,
        mean: result.mean,
        sd: result.sd,
        skills: result.skills,
        answered: result.answered,
        correct: result.correct,
        startLessonId: start?.lessonId ?? null,
        bookId: start?.bookId ?? null,
        note: start?.note ?? null,
      },
    };
    void savePlacement(userId, record);
    playFeedback('complete');
    setPhase({ kind: 'result', result, record });
  };

  const answer = (item: PlacementItem, option: string | null) => {
    const outcome: PlacementOutcome = option === null ? 'dontknow' : option === item.answer ? 'correct' : 'wrong';
    setPicked({ option, outcome });
    if (outcome === 'correct') {
      playFeedback('correct');
      setCat('happy');
    } else {
      if (outcome === 'wrong') playFeedback('wrong');
      setCat(outcome === 'wrong' ? 'sad' : 'thinking');
    }
    setResponses((rs) => [...rs, { itemId: item.id, level: item.level, skill: item.skill, options: item.options.length, outcome }]);
  };

  const next = () => {
    setPicked(null);
    setCat('idle');
    if (shouldStop(responses, levels)) {
      finish(responses);
      return;
    }
    const item = nextItem(bank, responses, seed, { listening: speakable });
    if (item) setPhase({ kind: 'question', item });
    else finish(responses);
  };

  if (phase.kind === 'intro') {
    const previous = cachedPlacements(userId)[lang];
    return (
      <div className="placement placement--center">
        <RoboCat size={120} state="happy" alive />
        <h1 className="t-screen">Определим уровень</h1>
        <p className="placement__lead">
          {language.titleRu}: 12–15 вопросов, 5–7 минут. Слова, грамматика, чтение и на слух.
        </p>
        <p className="t-meta">Не знаете ответ — жмите «Не знаю»: так результат будет точнее, чем если угадывать.</p>
        {previous && <p className="t-meta">Прошлый результат: ≈ {previous.level_label}</p>}
        <div className="placement__actions">
          <button type="button" className="btn btn--primary btn--block" onClick={startQuestions}>
            Начать тест
          </button>
          <button type="button" className="btn btn--text" onClick={previous ? close : skip}>
            {previous ? 'Назад' : 'Пропустить'}
          </button>
        </div>
      </div>
    );
  }

  if (phase.kind === 'result') {
    return <PlacementResultView result={phase.result} record={phase.record} lang={lang} onDone={close} />;
  }

  const { item } = phase;
  const answered = responses.length - (picked ? 1 : 0);
  const correctOption = picked ? item.answer : null;

  return (
    <div className="lesson placement">
      <header className="lesson__head">
        <button type="button" className="icon-btn" onClick={close} aria-label="Закрыть тест">
          <Icon name="close" size={26} />
        </button>
        <div
          className="progress lesson__progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={PLACEMENT_MAX_ITEMS}
          aria-valuenow={answered}
          aria-label="Прогресс теста"
        >
          <div className="progress__fill" style={{ width: `${(answered / PLACEMENT_MAX_ITEMS) * 100}%` }} />
        </div>
      </header>
      <p className="lesson__chapter">
        Тест на уровень · {SKILL_RU[item.skill]} · вопрос {answered + 1}
      </p>

      <section className="placement__card" aria-live="polite">
        <div className="placement__cat">
          <RoboCat size={52} state={cat} />
        </div>
        <p className="placement__prompt">{item.prompt}</p>
        {item.skill === 'listening' && item.audio ? (
          <ListenButton key={item.id} text={item.audio} lang={language.speechLang} />
        ) : (
          item.text && (
            <p className={`placement__text${lang === 'zh' ? ' han' : ''}${item.text.length > 40 ? ' placement__text--long' : ''}`} lang={language.speechLang}>
              {item.text}
            </p>
          )
        )}
      </section>

      <div className="options" role="radiogroup" aria-label="Варианты ответа">
        {item.options.map((option) => {
          const state = picked
            ? option === correctOption
              ? ' option--correct'
              : option === picked.option
                ? ' option--wrong'
                : ''
            : '';
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={picked?.option === option}
              className={`option${option.length > 14 ? ' option--long' : ''}${state}`}
              disabled={picked !== null}
              onClick={() => answer(item, option)}
              lang={/[㐀-鿿]/.test(option) ? 'zh-CN' : undefined}
            >
              <span className={/[㐀-鿿]/.test(option) ? 'han' : undefined}>{option}</span>
            </button>
          );
        })}
      </div>

      {picked && (
        <p className={`placement__explain placement__explain--${picked.outcome}`}>
          <strong>
            {picked.outcome === 'correct' ? 'Верно. ' : picked.outcome === 'wrong' ? 'Не совсем. ' : 'Ничего страшного. '}
          </strong>
          {item.explanation_ru}
        </p>
      )}

      <div className="lesson__footer">
        {picked ? (
          <button type="button" className="btn btn--primary btn--block" onClick={next}>
            Дальше
          </button>
        ) : (
          <button type="button" className="btn btn--secondary btn--block" onClick={() => answer(item, null)}>
            Не знаю
          </button>
        )}
      </div>
    </div>
  );
}

function ListenButton({ text, lang }: { text: string; lang: string }) {
  // Plays once when the question opens; the button repeats it.
  useEffect(() => {
    speak(text, lang);
  }, [text, lang]);
  return (
    <button type="button" className="listen-btn" onClick={() => speak(text, lang)} aria-label="Прослушать ещё раз">
      <Icon name="speaker" size={30} />
      <span>Послушать ещё раз</span>
    </button>
  );
}

function PlacementResultView({
  result,
  record,
  lang,
  onDone,
}: {
  result: PlacementResult;
  record: PlacementRecord;
  lang: string;
  onDone: () => void;
}) {
  const { lessons, books } = useLoadedData();
  const start = lessons.find((l) => l.id === record.score.startLessonId) ?? null;
  const book = books.find((b) => b.id === record.score.bookId) ?? null;
  const strong = (Object.entries(result.skills) as [PlacementSkill, string][]).filter(([, v]) => v === 'strong').map(([k]) => SKILL_RU_LOWER[k]);
  const weak = (Object.entries(result.skills) as [PlacementSkill, string][]).filter(([, v]) => v === 'weak').map(([k]) => SKILL_RU_LOWER[k]);

  return (
    <div className="placement placement--center placement--result">
      <RoboCat size={120} state="happy" alive />
      <p className="placement__caps">Ваш уровень</p>
      <h1 className="placement__level">≈ {result.label}</h1>
      <p className="t-meta">
        Верно {result.correct} из {result.answered}
      </p>
      {(strong.length > 0 || weak.length > 0) && (
        <ul className="placement__skills">
          {strong.length > 0 && <li>Сильная сторона: {strong.join(', ')}</li>}
          {weak.length > 0 && <li>Подтянуть: {weak.join(', ')}</li>}
        </ul>
      )}
      {start && (
        <div className="placement__start">
          <p className="placement__caps">Начните с</p>
          <p className="placement__start-title">
            <span className="han">{book?.title ?? ''}</span> · урок {start.number}
          </p>
          <p className="t-meta">Уроки до него открыты — можно проходить в любом порядке.</p>
        </div>
      )}
      {record.score.note && <p className="placement__note">{record.score.note}</p>}
      <p className="t-meta placement__honest">Короткий тест даёт примерный уровень (± полуровня) и не проверяет говорение.</p>
      {getGeminiKey() && <OpenAnswer lang={lang} level={result.label} />}
      <div className="placement__actions">
        <button type="button" className="btn btn--primary btn--block" onClick={onDone}>
          Начать учиться
        </button>
      </div>
    </div>
  );
}

/** Optional: a few sentences of free writing, judged by the tutor (not stored). */
function OpenAnswer({ lang, level }: { lang: string; level: string }) {
  const language = languageInfo(lang);
  const [text, setText] = useState('');
  const [state, setState] = useState<{ kind: 'idle' } | { kind: 'busy' } | { kind: 'done'; level: string; comment: string } | { kind: 'error' }>({ kind: 'idle' });
  const [open, setOpen] = useState(false);

  const check = async () => {
    setState({ kind: 'busy' });
    const scale = lang === 'zh' ? 'HSK 1–6' : 'CEFR A1–C1';
    const prompt = [
      `Ты экзаменатор по языку «${language.adjectiveRu}». Ученик написал короткий текст о себе.`,
      `Оцени уровень письма по шкале ${scale}. Тест с вариантами ответа дал ≈ ${level}.`,
      'Ответь JSON: {"level": "<уровень по шкале>", "comment": "<2–3 предложения по-русски: что хорошо, 1–2 главные ошибки с исправлением>"}.',
      `Текст ученика:\n"""${text.slice(0, 1200)}"""`,
    ].join('\n');
    try {
      const raw = await askJson(prompt);
      const parsed = JSON.parse(raw) as { level?: unknown; comment?: unknown };
      if (typeof parsed.level !== 'string' || typeof parsed.comment !== 'string') throw new Error('bad');
      setState({ kind: 'done', level: parsed.level, comment: parsed.comment });
    } catch {
      setState({ kind: 'error' });
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn btn--secondary btn--block" onClick={() => setOpen(true)}>
        Проверить письмо с репетитором (необязательно)
      </button>
    );
  }
  return (
    <section className="placement__open card stack">
      <label className="t-meta" htmlFor="placement-open">
        Напишите 2–3 предложения о себе на языке «{language.adjectiveRu}» — репетитор оценит письмо.
      </label>
      <textarea
        id="placement-open"
        className="input placement__textarea"
        lang={language.speechLang}
        rows={4}
        maxLength={1200}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {state.kind === 'done' && (
        <p>
          <strong>Письмо ≈ {state.level}.</strong> {state.comment}
        </p>
      )}
      {state.kind === 'error' && <p className="t-meta">Репетитор сейчас не ответил — попробуйте позже.</p>}
      <button
        type="button"
        className="btn btn--ink btn--block"
        disabled={text.trim().length < 10 || state.kind === 'busy'}
        onClick={() => void check()}
      >
        {state.kind === 'busy' ? 'Проверяем…' : 'Проверить'}
      </button>
    </section>
  );
}

/** Home card offering the test when this language has no result and no finished lessons. */
export function PlacementOffer() {
  const { navigate } = useRouter();
  const userId = useSession().session?.user.id ?? '';
  const { activeCourse } = useAppData();
  const { progress, lessons } = useLoadedData();
  const placements = usePlacements(userId);
  const lang = activeCourse?.lang ?? '';
  const [dismissed, setDismissed] = useState(() => placementDismissed(userId, lang));
  useEffect(() => setDismissed(placementDismissed(userId, lang)), [userId, lang]);
  const started = useMemo(() => lessons.some((l) => progress[l.id]), [lessons, progress]);
  if (!PLACEMENT_LANGS.includes(lang) || placements[lang] || dismissed || started) return null;
  return (
    <section className="placement-offer card">
      <RoboCat size={56} state="happy" />
      <div className="placement-offer__text">
        <h2 className="t-card">Определить уровень?</h2>
        <p className="t-meta">5–7 минут — и уроки ниже вашего уровня откроются сразу.</p>
      </div>
      <div className="placement-offer__actions">
        <button type="button" className="btn btn--primary" onClick={() => navigate(routes.placement)}>
          Пройти тест
        </button>
        <button
          type="button"
          className="btn btn--text"
          onClick={() => {
            dismissPlacement(userId, lang);
            setDismissed(true);
          }}
        >
          Не сейчас
        </button>
      </div>
    </section>
  );
}

/** Profile: the latest level per language and «Пройти заново». */
export function PlacementLevels() {
  const { navigate } = useRouter();
  const userId = useSession().session?.user.id ?? '';
  const { courses } = useLoadedData();
  const { activeCourse, setActiveCourse } = useAppData();
  const placements = usePlacements(userId);
  const langs = PLACEMENT_LANGS.filter((lang) => courses.some((c) => c.lang === lang));
  if (langs.length === 0) return null;
  const retake = (lang: string) => {
    const course = courses.find((c) => c.lang === lang);
    if (course && course.id !== activeCourse?.id) setActiveCourse(course.id);
    navigate(routes.placement);
  };
  return (
    <section className="card stack" aria-labelledby="profile-level">
      <h2 id="profile-level" className="t-section">
        Уровень
      </h2>
      <ul className="placement-levels">
        {langs.map((lang) => {
          const record = placements[lang];
          return (
            <li key={lang} className="placement-levels__row">
              <span className="placement-levels__info">
                <span className="placement-levels__lang">{languageInfo(lang).titleRu}</span>
                <span className="placement-levels__level">{record ? `≈ ${record.level_label}` : 'не пройден'}</span>
              </span>
              <button type="button" className="btn btn--secondary placement-levels__btn" onClick={() => retake(lang)}>
                {record ? 'Пройти заново' : 'Пройти тест'}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
